import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  fetchContestantPlayers,
  fetchMatchById,
  fetchMatchStats,
  fetchMatchSeasonStats,
} from '@/lib/network';
import { MatchData, EventsByCategory } from '../types';
import { ContestantPlayer } from '@/lib/network';
import { DEFAULT_STATS, FORMATION_PLACE_ORDER } from '../constants';
import { toEventsByCategory, mapPlayers } from '../utils';
import { Player } from '../types';
import { getMatchStatusDisplay } from '@/lib/matchStatus';
import { getDisplayCompetitionName } from '@/lib/competitionLabels';

function normalizePossession(home: number, away: number): [number, number] {
  const homeRounded = Math.round(home);
  const awayRounded = Math.round(away);
  const sum = homeRounded + awayRounded;
  if (sum === 100) {
    return [homeRounded, awayRounded];
  }
  // Adjust the larger value so the two always sum to 100
  if (homeRounded >= awayRounded) {
    return [homeRounded + (100 - sum), awayRounded];
  }
  return [homeRounded, awayRounded + (100 - sum)];
}

function filterEventSectionsForFantasyTeam(
  sections: EventsByCategory[],
  homeHasFantasyData: boolean,
  awayHasFantasyData: boolean
): EventsByCategory[] {
  return sections
    .map(section => ({
      ...section,
      homeEvents: homeHasFantasyData ? section.homeEvents : [],
      awayEvents: awayHasFantasyData ? section.awayEvents : [],
    }))
    .filter(section => section.homeEvents.length > 0 || section.awayEvents.length > 0);
}

function normalizePosition(pos: string | undefined): 'GK' | 'DF' | 'MF' | 'FW' | null {
  if (!pos) return null;
  const p = pos.toUpperCase().trim();
  if (p === 'GK' || p === 'G') return 'GK';
  if (
    p === 'DF' ||
    p === 'DEF' ||
    p === 'D' ||
    p === 'CB' ||
    p === 'FB' ||
    p === 'LB' ||
    p === 'RB'
  )
    return 'DF';
  if (
    p === 'MF' ||
    p === 'MID' ||
    p === 'M' ||
    p === 'CM' ||
    p === 'DM' ||
    p === 'AM' ||
    p === 'LM' ||
    p === 'RM' ||
    p === 'WM'
  )
    return 'MF';
  if (
    p === 'FW' ||
    p === 'STR' ||
    p === 'F' ||
    p === 'ST' ||
    p === 'CF' ||
    p === 'LF' ||
    p === 'RF' ||
    p === 'WF'
  )
    return 'FW';
  return null;
}

function deriveFormation(players: Player[]): number[] {
  const counts: Record<string, number> = { GK: 0, DF: 0, MF: 0, FW: 0 };
  let unknownCount = 0;
  for (const p of players) {
    const pos = normalizePosition(p.position);
    if (pos) {
      counts[pos]++;
    } else {
      unknownCount++;
    }
  }
  const total = counts.GK + counts.DF + counts.MF + counts.FW + unknownCount;
  if (total === 0) return [1, 4, 3, 3];
  // Distribute unknown players into the formation
  // Default: add to midfield, then defense, then attack
  const formation: number[] = [
    counts.GK || (unknownCount > 0 ? (unknownCount--, 1) : 0),
    counts.DF || 0,
    counts.MF || 0,
    counts.FW || 0,
  ];
  // Distribute remaining unknowns: prioritize MF, then DF, then FW
  while (unknownCount > 0) {
    if (formation[2] < 5) {
      formation[2]++;
      unknownCount--;
    } else if (formation[1] < 5) {
      formation[1]++;
      unknownCount--;
    } else if (formation[3] < 4) {
      formation[3]++;
      unknownCount--;
    } else {
      formation[1]++;
      unknownCount--;
    }
  }
  // Ensure at least 1 GK
  if (formation[0] === 0) {
    formation[0] = 1;
    // Take from the largest row
    const maxIdx = formation.indexOf(Math.max(...formation.slice(1)));
    if (formation[maxIdx] > 0) formation[maxIdx]--;
  }
  return formation;
}

interface UseMatchDataProps {
  matchId: string | undefined;
}

interface UseMatchDataResult {
  displayMatch: MatchData | null;
  isLoading: boolean;
  homeHasLineup: boolean;
  awayHasLineup: boolean;
  isError: boolean;
  errorMessage: string | null;
}

export function useMatchData({ matchId }: UseMatchDataProps): UseMatchDataResult {
  const matchQuery = useQuery({
    queryKey: ['match', 'detail', matchId],
    queryFn: () => {
      if (!matchId) throw new Error('matchId is required');
      return fetchMatchById(matchId);
    },
    enabled: !!matchId,
  });

  const statsQuery = useQuery({
    queryKey: ['match', 'stats', matchId],
    queryFn: () => {
      if (!matchId) throw new Error('matchId is required');
      return fetchMatchStats(matchId);
    },
    enabled: !!matchId,
  });

  const seasonStatsQuery = useQuery({
    queryKey: ['match', 'seasonStats', matchId],
    queryFn: () => {
      if (!matchId) throw new Error('matchId is required');
      return fetchMatchSeasonStats(matchId);
    },
    enabled: !!matchId,
  });

  const homeContestantId = matchQuery.data?.data.homeContestant.id;
  const awayContestantId = matchQuery.data?.data.awayContestant.id;

  const homePlayersQuery = useQuery({
    queryKey: ['match', 'contestantPlayers', matchId, homeContestantId],
    queryFn: () => {
      if (!matchId || !homeContestantId) throw new Error('Missing parameters');
      return fetchContestantPlayers(matchId, homeContestantId);
    },
    enabled: !!matchId && !!homeContestantId,
  });

  const awayPlayersQuery = useQuery({
    queryKey: ['match', 'contestantPlayers', matchId, awayContestantId],
    queryFn: () => {
      if (!matchId || !awayContestantId) throw new Error('Missing parameters');
      return fetchContestantPlayers(matchId, awayContestantId);
    },
    enabled: !!matchId && !!awayContestantId,
  });

  const homeHasLineup =
    !!homePlayersQuery.data?.data.lineup?.startingXi &&
    homePlayersQuery.data.data.lineup.startingXi.length > 0;
  const awayHasLineup =
    !!awayPlayersQuery.data?.data.lineup?.startingXi &&
    awayPlayersQuery.data.data.lineup.startingXi.length > 0;

  const displayMatch = useMemo<MatchData | null>(() => {
    const match = matchQuery.data?.data;
    const stats = statsQuery.data?.data;
    const seasonStats = seasonStatsQuery.data?.data;
    const homePlayers = (homePlayersQuery.data?.data.players ?? []) as ContestantPlayer[];
    const awayPlayers = (awayPlayersQuery.data?.data.players ?? []) as ContestantPlayer[];
    const homeLineup = homePlayersQuery.data?.data.lineup;
    const awayLineup = awayPlayersQuery.data?.data.lineup;

    if (!match) return null;

    const competition = getDisplayCompetitionName(match.tournamentCalendarName, '');
    const rawStatus = match.status ?? 'fixture';
    const hasScore = match.homeScore !== null && match.awayScore !== null;
    const statusDisplay = getMatchStatusDisplay(rawStatus, hasScore);

    const homeTeamCode =
      match.homeContestant.shirtKey || match.homeContestant.shortName || match.homeContestant.name;
    const awayTeamCode =
      match.awayContestant.shirtKey || match.awayContestant.shortName || match.awayContestant.name;

    const mappedHomePlayers = mapPlayers(homePlayers, homeTeamCode);
    const mappedAwayPlayers = mapPlayers(awayPlayers, awayTeamCode);

    const eventSections = toEventsByCategory(
      match.events,
      match.homeContestant.id,
      match.awayContestant.id
    );

    const buildLineup = (
      mappedPlayers: Player[],
      lineup:
        | {
            formation: string | null;
            startingXi: string[] | null;
            formationPlace: number[] | null;
            substitutes: string[] | null;
          }
        | undefined
    ) => {
      if (!lineup?.startingXi || lineup.startingXi.length === 0) {
        return {
          formation: deriveFormation(mappedPlayers.slice(0, 11)),
          players: mappedPlayers.slice(0, 11),
          subs: mappedPlayers.slice(11),
        };
      }
      // lineup IDs may be either personId or fantasyPlayerId depending on how they were saved
      const resolvePlayer = (id: string) =>
        mappedPlayers.find(p => p.id === id || p.fantasyPlayerId === id);

      const startPlayers = lineup.startingXi
        .map((id, index) => {
          const player = resolvePlayer(id);
          if (!player) return null;
          return {
            ...player,
            formationPlace: lineup.formationPlace?.[index] ?? index + 1,
          };
        })
        .filter(Boolean) as Player[];
      // The bench is exactly the match-day substitutes. The player list also
      // carries the rest of the club's roster (including players who have since
      // left), so anyone the lineup does not name must not be shown as a sub.
      const lineupSubs = (lineup.substitutes ?? [])
        .map(id => resolvePlayer(id))
        .filter(Boolean) as Player[];
      // Subs whose own club has a fixture in the current gameweek lead the list;
      // everyone else follows, both groups in lineup order.
      const subPlayers = [
        ...lineupSubs.filter(p => p.playsInCurrentGameweek),
        ...lineupSubs.filter(p => !p.playsInCurrentGameweek),
      ];
      const formationString = lineup.formation;
      let formation: number[];
      let normalizedFormation: string | null = null;
      if (formationString) {
        let normalized = formationString;
        // Preserve 'd' suffix for diamond variants (e.g. '343d' -> '3-4-3d')
        const isDiamond = normalized.endsWith('d');
        if (isDiamond) {
          normalized = normalized.slice(0, -1);
        }
        if (!normalized.includes('-') && /^\d+$/.test(normalized)) {
          normalized = normalized.split('').join('-');
        }
        if (isDiamond) {
          normalized += 'd';
        }
        normalizedFormation = normalized;
        if (normalized === '3-4-3d') {
          // 3-4-3 diamond: render as 6 rows (1 GK + 3 DF + 1 DM + 2 CM + 1 AM + 3 FW)
          formation = [1, 3, 1, 2, 1, 3];
        } else if (normalized.includes('-')) {
          const parts = normalized.split('-').map(n => parseInt(n, 10));
          formation = [1, ...parts];
        } else {
          formation = deriveFormation(startPlayers);
        }
      } else {
        formation = deriveFormation(startPlayers);
      }
      // Reorder players based on formationPlace mapping
      const placeOrder =
        normalizedFormation && FORMATION_PLACE_ORDER[normalizedFormation]
          ? FORMATION_PLACE_ORDER[normalizedFormation]
          : Array.from({ length: 11 }, (_, i) => i + 1);
      const playerByPlace = new Map(startPlayers.map(p => [p.formationPlace ?? 0, p]));
      const orderedPlayers = placeOrder
        .map(fp => playerByPlace.get(fp))
        .filter(Boolean) as Player[];
      return {
        formation,
        players: orderedPlayers.length > 0 ? orderedPlayers : startPlayers,
        subs: subPlayers,
      };
    };

    const homeLineupData = buildLineup(mappedHomePlayers, homeLineup);
    const awayLineupData = buildLineup(mappedAwayPlayers, awayLineup);

    const homeHasFantasyData = mappedHomePlayers.some(p => p.fantasyPlayerId);
    const awayHasFantasyData = mappedAwayPlayers.some(p => p.fantasyPlayerId);
    const isAdditionalLeagueFixture = !match.matchDetailAvailable;

    const filteredEventSections = filterEventSectionsForFantasyTeam(
      eventSections,
      homeHasFantasyData,
      awayHasFantasyData
    );

    return {
      id: match.id,
      competition,
      isAdditionalLeagueFixture,
      homeHasFantasyData,
      awayHasFantasyData,
      homeTeam: {
        name: match.homeContestant.name,
        shortName: match.homeContestant.shortName || match.homeContestant.name,
        code: homeTeamCode,
        flag: match.homeContestant.contestantFlagKey || undefined,
        score: match.homeScore ?? 0,
      },
      awayTeam: {
        name: match.awayContestant.name,
        shortName: match.awayContestant.shortName || match.awayContestant.name,
        code: awayTeamCode,
        flag: match.awayContestant.contestantFlagKey || undefined,
        score: match.awayScore ?? 0,
      },
      status: statusDisplay.label,
      statusVariant: statusDisplay.statusVariant,
      rawStatus,
      kickoffAt: match.kickoffAt || '',
      hasStarted: statusDisplay.hasStarted,
      venue: '',
      eventSections: filteredEventSections,
      homeFormation: homeLineupData.formation,
      awayFormation: awayLineupData.formation,
      homePlayers: homeLineupData.players,
      awayPlayers: awayLineupData.players,
      homeSubs: homeLineupData.subs,
      awaySubs: awayLineupData.subs,
      stats: stats
        ? {
            possession: normalizePossession(stats.home.possession, stats.away.possession),
            passCompletion: [
              Math.round(stats.home.passCompletionRate),
              Math.round(stats.away.passCompletionRate),
            ],
            shots: [Math.round(stats.home.shots), Math.round(stats.away.shots)],
            shotsOnTarget: [
              Math.round(stats.home.shotsOnTarget),
              Math.round(stats.away.shotsOnTarget),
            ],
            corners: [Math.round(stats.home.corners), Math.round(stats.away.corners)],
            fouls: [Math.round(stats.home.fouls), Math.round(stats.away.fouls)],
            tackles: [Math.round(stats.home.tackles), Math.round(stats.away.tackles)],
            saves: [Math.round(stats.home.saves), Math.round(stats.away.saves)],
            yellowCards: [Math.round(stats.home.yellowCards), Math.round(stats.away.yellowCards)],
            redCards: [Math.round(stats.home.redCards), Math.round(stats.away.redCards)],
          }
        : DEFAULT_STATS,
      seasonStats: seasonStats
        ? [
            {
              label: 'Fantasy Points',
              homeValue: seasonStats.home.fantasyPoints,
              awayValue: seasonStats.away.fantasyPoints,
            },
            {
              label: 'Goals',
              homeValue: seasonStats.home.goals,
              awayValue: seasonStats.away.goals,
            },
            {
              label: 'Shots',
              homeValue: seasonStats.home.shots,
              awayValue: seasonStats.away.shots,
            },
            {
              label: 'Goals Conceded',
              homeValue: seasonStats.home.goalsConceded,
              awayValue: seasonStats.away.goalsConceded,
            },
            {
              label: 'Clean Sheets',
              homeValue: seasonStats.home.cleanSheets,
              awayValue: seasonStats.away.cleanSheets,
            },
          ]
        : undefined,
    };
  }, [
    matchQuery.data,
    statsQuery.data,
    seasonStatsQuery.data,
    homePlayersQuery.data,
    awayPlayersQuery.data,
  ]);

  // Surface the API error message (e.g. the 403 for additional-league
  // fixtures) so the page can render a "navigate back" state instead of an
  // ambiguous infinite loader.
  const errorMessage = useMemo(() => {
    const err = matchQuery.error;
    if (axios.isAxiosError<{ message?: string }>(err)) {
      return err.response?.data?.message ?? err.message ?? null;
    }
    if (err instanceof Error) return err.message;
    return null;
  }, [matchQuery.error]);

  return {
    displayMatch,
    isLoading:
      matchQuery.isLoading ||
      statsQuery.isLoading ||
      seasonStatsQuery.isLoading ||
      homePlayersQuery.isLoading ||
      awayPlayersQuery.isLoading,
    homeHasLineup,
    awayHasLineup,
    isError: matchQuery.isError,
    errorMessage,
  };
}
